<template>
  <router-view />
  <BaseToast />

  <button
    v-if="!isAuthenticated"
    type="button"
    class="theme-toggle-btn"
    @click="toggleTheme"
    :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
    :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
  >
    <Sun v-if="isDarkMode" class="h-5 w-5" />
    <Moon v-else class="h-5 w-5" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useColorScheme } from '@/composables/ui/useColorScheme'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import BaseToast from '@/components/ui/BaseToast.vue'
import { Sun, Moon } from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Diasporiques ABA Solutions LLC')
useTitle(appTitle.value, { suffix: false })

useColorScheme()

const themeStore = useThemeStore()
const authStore = useAuthStore()
const isDarkMode = computed(() => themeStore.isDarkMode)
const isAuthenticated = computed(() => authStore.isAuthenticated)

function toggleTheme() {
  themeStore.toggleTheme()
}
</script>

<style>
/* Global styles */
html {
  font-family: 'Poppins', sans-serif;
}

body {
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Dark mode styles */
.dark {
  color-scheme: dark;
}

/* Color scheme transitions */
* {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Global theme toggle button */
.theme-toggle-btn {
  @apply fixed top-4 right-4 z-50
         h-10 w-10
         flex items-center justify-center
         rounded-full
         bg-white dark:bg-gray-800
         border border-gray-200 dark:border-gray-700
         text-gray-700 dark:text-gray-300
         shadow-lg hover:shadow-xl
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-all duration-200
         cursor-pointer;
}
</style> 